import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useGlobalStore } from '@digiwedge/global-state';
import { HttpRequestError, addMyFavorite, listMyFavorites, removeMyFavorite, } from '@digiwedge/teetime-api-client';
import { useFavouritesStore } from './useFavouritesStore.js';
const normaliseError = (error, fallback) => error instanceof Error ? error : new Error(fallback);
const toClubIds = (records) => records
    .map((fav) => (typeof fav.clubId === 'string' ? fav.clubId.trim() : ''))
    .filter((clubId) => clubId.length > 0);
const toClubDetails = (records) => {
    const details = [];
    for (const fav of records) {
        const clubId = typeof fav.clubId === 'string' ? fav.clubId.trim() : '';
        if (!clubId) {
            continue;
        }
        const club = fav.club;
        details.push({
            clubId,
            clubCode: typeof club?.clubCode === 'string' ? club.clubCode : undefined,
            name: typeof club?.name === 'string' ? club.name : undefined,
            address: typeof club?.address === 'string' ? club.address : undefined,
            logoUrl: typeof club?.logoUrl === 'string' ? club.logoUrl : undefined,
        });
    }
    return details;
};
const extractClubDetails = (record) => toClubDetails([record])[0] ?? null;
export const useFavourites = ({ enabled = true } = {}) => {
    const favourites = useFavouritesStore((state) => state.favourites);
    const setAll = useFavouritesStore((state) => state.setAll);
    const clubDetailsMap = useFavouritesStore((state) => state.clubDetails);
    const favouriteClubs = useMemo(() => favourites.map((clubId) => clubDetailsMap[clubId] ?? { clubId }), [clubDetailsMap, favourites]);
    const favouriteClubIdByCode = useMemo(() => {
        const map = {};
        for (const fav of favouriteClubs) {
            const code = typeof fav.clubCode === 'string' && fav.clubCode.trim().length > 0 ? fav.clubCode.trim() : '';
            if (!code) {
                continue;
            }
            map[code] = fav.clubId;
        }
        return map;
    }, [favouriteClubs]);
    const isAuthenticated = useGlobalStore((state) => state.isAuthenticated);
    const [loading, setLoading] = useState(false);
    const [updatingClubId, setUpdatingClubId] = useState(null);
    const [error, setError] = useState(null);
    const [disabled, setDisabled] = useState(false);
    const isFeatureEnabled = enabled !== false;
    const fetchKeyRef = useRef(null);
    const isMountedRef = useRef(true);
    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);
    const clearStore = useCallback(() => {
        const state = useFavouritesStore.getState();
        if (state.favourites.length === 0 && Object.keys(state.clubDetails).length === 0) {
            return;
        }
        setAll([]);
    }, [setAll]);
    const fetchFavourites = useCallback(async () => {
        if (!isAuthenticated || disabled) {
            clearStore();
            if (isMountedRef.current) {
                setError(null);
                setLoading(false);
            }
            return;
        }
        if (isMountedRef.current) {
            setLoading(true);
            setError(null);
        }
        try {
            const favouritesResponse = await listMyFavorites();
            setAll(toClubIds(favouritesResponse), toClubDetails(favouritesResponse));
        }
        catch (err) {
            if (err instanceof HttpRequestError && err.status === 500) {
                const body = err.bodyText ?? err.message;
                if (typeof body === 'string' && body.includes('UserIdentity')) {
                    clearStore();
                    if (isMountedRef.current) {
                        setDisabled(true);
                        setError(null);
                    }
                    return;
                }
            }
            const normalised = normaliseError(err, 'Failed to load favourites');
            if (isMountedRef.current) {
                setError(normalised);
            }
            clearStore();
        }
        finally {
            if (isMountedRef.current) {
                setLoading(false);
            }
        }
    }, [clearStore, disabled, isAuthenticated, setAll]);
    useEffect(() => {
        if (!isFeatureEnabled) {
            return;
        }
        if (!isAuthenticated || disabled) {
            fetchKeyRef.current = null;
            if (isMountedRef.current) {
                setError(null);
                setLoading(false);
            }
            clearStore();
            return;
        }
        const nextFetchKey = `me-${disabled ? 'disabled' : 'active'}`;
        if (fetchKeyRef.current === nextFetchKey) {
            return;
        }
        fetchKeyRef.current = nextFetchKey;
        void fetchFavourites();
    }, [clearStore, disabled, fetchFavourites, isAuthenticated, isFeatureEnabled]);
    const toggleFavourite = useCallback(async (clubIdRaw) => {
        if (!isFeatureEnabled) {
            throw new Error('Favourites are disabled');
        }
        if (!isAuthenticated || disabled) {
            throw new Error('Cannot update favourites when unauthenticated');
        }
        const clubId = clubIdRaw.trim();
        if (!clubId) {
            throw new Error('Club identifier is required');
        }
        if (isMountedRef.current) {
            setUpdatingClubId(clubId);
            setError(null);
        }
        const { add, remove, isFavourite: isFavouriteLocal } = useFavouritesStore.getState();
        try {
            if (isFavouriteLocal(clubId)) {
                await removeMyFavorite(clubId);
                remove(clubId);
            }
            else {
                const favourite = await addMyFavorite(clubId);
                const details = extractClubDetails(favourite);
                add(favourite.clubId, details);
            }
        }
        catch (err) {
            const normalised = normaliseError(err, 'Failed to update favourites');
            if (isMountedRef.current) {
                setError(normalised);
            }
            throw normalised;
        }
        finally {
            if (isMountedRef.current) {
                setUpdatingClubId((current) => (current === clubId ? null : current));
            }
        }
    }, [disabled, isAuthenticated, isFeatureEnabled]);
    const isFavourite = useCallback((clubId) => useFavouritesStore.getState().isFavourite(clubId), []);
    const refetch = isFeatureEnabled ? fetchFavourites : async () => {
        setAll([]);
        setError(null);
    };
    return {
        favourites,
        favouriteClubs,
        favouriteClubIdByCode,
        loading,
        updatingClubId,
        error,
        isFavourite,
        toggleFavourite,
        refetch,
    };
};
//# sourceMappingURL=useFavourites.js.map